const MODIFIER_ALIASES: Record<string, string> = {
  control: 'ctrl',
  cmd: 'meta',
  command: 'meta',
  option: 'alt',
};

const MODIFIER_ORDER = ['ctrl', 'alt', 'shift', 'meta'] as const;

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /mac/i.test(navigator.platform || navigator.userAgent);
}

/** Normalize combo string to canonical `ctrl+shift+s` form. */
export function normalizeCombo(combo: string, metaAsCtrlOnMac = false): string {
  const parts = combo
    .trim()
    .split('+')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  const modifiers = new Set<string>();
  let key = '';

  for (const part of parts) {
    const alias = MODIFIER_ALIASES[part] ?? part;
    if (MODIFIER_ORDER.includes(alias as (typeof MODIFIER_ORDER)[number])) {
      if (alias === 'meta' && metaAsCtrlOnMac && isMacPlatform()) {
        modifiers.add('ctrl');
      } else {
        modifiers.add(alias);
      }
      continue;
    }
    key = normalizeKeyPart(alias);
  }

  const modPart = MODIFIER_ORDER.filter((mod) => modifiers.has(mod)).join('+');
  return modPart ? `${modPart}+${key}` : key;
}

export function normalizeCombos(
  combo: string | string[],
  metaAsCtrlOnMac = false,
): string[] {
  const list = Array.isArray(combo) ? combo : [combo];
  return [...new Set(list.map((item) => normalizeCombo(item, metaAsCtrlOnMac)))];
}

function normalizeKeyPart(key: string): string {
  if (key === ' ') return 'space';
  if (key === 'esc') return 'escape';
  if (key.length === 1) return key.toLowerCase();
  return key.toLowerCase();
}

/** Build combo string from a keyboard event. */
export function eventToCombo(
  event: KeyboardEvent,
  metaAsCtrlOnMac = false,
): string {
  const modifiers: string[] = [];

  if (event.ctrlKey) {
    modifiers.push('ctrl');
  }
  if (event.altKey) {
    modifiers.push('alt');
  }
  if (event.shiftKey) {
    modifiers.push('shift');
  }
  if (event.metaKey) {
    if (metaAsCtrlOnMac && isMacPlatform()) {
      if (!modifiers.includes('ctrl')) {
        modifiers.push('ctrl');
      }
    } else {
      modifiers.push('meta');
    }
  }

  const key = normalizeKeyPart(event.key);
  const modPart = modifiers.join('+');
  return modPart ? `${modPart}+${key}` : key;
}

export function combosMatch(
  eventCombo: string,
  registeredCombo: string,
  metaAsCtrlOnMac = false,
): boolean {
  const eventNorm = normalizeCombo(eventCombo, metaAsCtrlOnMac);
  const registeredNorm = normalizeCombo(registeredCombo, metaAsCtrlOnMac);
  if (eventNorm === registeredNorm) {
    return true;
  }

  // `?` is often emitted as `shift+/` on US keyboards.
  if (registeredNorm === '?' && eventNorm === 'shift+/') {
    return true;
  }
  if (registeredNorm === 'shift+/' && eventNorm === 'shift+?') {
    return true;
  }

  return false;
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    const input = target as HTMLInputElement;
    if (tag === 'input') {
      const type = (input.type || 'text').toLowerCase();
      if (['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color'].includes(type)) {
        return false;
      }
    }
    return true;
  }

  return Boolean(target.closest('[contenteditable="true"]'));
}

export function isIgnoredTag(
  target: EventTarget | null,
  ignoreTags: string[],
): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName.toLowerCase();
  return ignoreTags.includes(tag);
}
